type FourCC = string;

export type AviVideoStreamProbeResult = {
  isAvi: boolean;
  videoStream?: {
    handler?: FourCC;
    compression?: FourCC;
  };
  reason?: string;
};

const DEFAULT_MAX_BYTES = 512 * 1024; // 512KB
const RETRY_MAX_BYTES = 2 * 1024 * 1024; // 2MB

function canRead(view: DataView, offset: number, size: number): boolean {
  return offset >= 0 && size >= 0 && offset + size <= view.byteLength;
}

function readFourCC(view: DataView, offset: number): FourCC | undefined {
  if (!canRead(view, offset, 4)) return undefined;
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3),
  );
}

function readU32LE(view: DataView, offset: number): number | undefined {
  if (!canRead(view, offset, 4)) return undefined;
  return view.getUint32(offset, true);
}

function chunkAdvance(size: number): number {
  // RIFF chunks are word-aligned.
  return size + (size % 2);
}

function parseStrlList(view: DataView, start: number, end: number): {
  streamType?: FourCC;
  handler?: FourCC;
  compression?: FourCC;
} {
  let streamType: FourCC | undefined;
  let handler: FourCC | undefined;
  let strfDataStart: number | undefined;
  let strfSize: number | undefined;

  let offset = start;
  while (offset + 8 <= end) {
    const id = readFourCC(view, offset);
    const size = readU32LE(view, offset + 4);
    if (!id || size === undefined) break;

    const dataStart = offset + 8;
    const dataEnd = dataStart + size;
    if (dataEnd > end) break;

    if (id === "strh") {
      // AVI Stream Header (AVISTREAMHEADER)
      // https://learn.microsoft.com/en-us/windows/win32/directshow/avi-stream-header
      // fccType (4) + fccHandler (4) are first 8 bytes.
      const t = readFourCC(view, dataStart);
      const h = readFourCC(view, dataStart + 4);
      if (t) streamType = t;
      if (h) handler = h;
    } else if (id === "strf") {
      strfDataStart = dataStart;
      strfSize = size;
    }

    offset = dataStart + chunkAdvance(size);
  }

  let compression: FourCC | undefined;
  if (streamType === "vids" && strfDataStart !== undefined && (strfSize ?? 0) >= 20) {
    // Video strf is BITMAPINFOHEADER; biCompression is at offset 16.
    compression = readFourCC(view, strfDataStart + 16);
  }

  return { streamType, handler, compression };
}

function findFirstAviVideoStream(view: DataView, start: number, end: number): { handler?: FourCC; compression?: FourCC } | undefined {
  let offset = start;
  while (offset + 8 <= end) {
    const id = readFourCC(view, offset);
    const size = readU32LE(view, offset + 4);
    if (!id || size === undefined) break;

    const dataStart = offset + 8;
    const dataEnd = dataStart + size;
    if (dataEnd > end) break;

    if (id === "LIST") {
      const listType = readFourCC(view, dataStart);
      const listContentsStart = dataStart + 4;
      const listContentsEnd = dataEnd;

      if (listType === "strl") {
        const stream = parseStrlList(view, listContentsStart, listContentsEnd);
        if (stream.streamType === "vids") {
          return { handler: stream.handler, compression: stream.compression };
        }
      }

      const nested = findFirstAviVideoStream(view, listContentsStart, listContentsEnd);
      if (nested) return nested;
    }

    offset = dataStart + chunkAdvance(size);
  }

  return undefined;
}

export async function probeAviVideoStream(file: Blob, opts?: { maxBytes?: number }): Promise<AviVideoStreamProbeResult> {
  const maxBytes = Math.max(64 * 1024, Math.floor(Number(opts?.maxBytes ?? DEFAULT_MAX_BYTES)));
  const sizesToTry: number[] = [maxBytes];
  const retryBytes = Math.min(RETRY_MAX_BYTES, Math.max(0, file.size));
  if (retryBytes > maxBytes) sizesToTry.push(retryBytes);

  let lastNoStream = false;

  for (const size of sizesToTry) {
    const buf = await file.slice(0, size).arrayBuffer();
    const view = new DataView(buf);

    const riff = readFourCC(view, 0);
    if (riff !== "RIFF") {
      return { isAvi: false, reason: "Not a RIFF container" };
    }

    const riffType = readFourCC(view, 8);
    if (riffType !== "AVI ") {
      return { isAvi: false, reason: "Not an AVI container" };
    }

    const riffSize = readU32LE(view, 4);
    const riffEnd = riffSize !== undefined ? Math.min(view.byteLength, 8 + riffSize) : view.byteLength;

    const video = findFirstAviVideoStream(view, 12, riffEnd);
    if (video) {
      return { isAvi: true, videoStream: video };
    }

    lastNoStream = true;
  }

  if (lastNoStream) {
    return { isAvi: true, reason: "No AVI video stream found" };
  }
  return { isAvi: false, reason: "Not an AVI container" };
}
